// src/pages/AdminSubscriptionsList.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import { Card } from "react-bootstrap";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";

import { BASE_URL, security_key as SECURITY_KEY } from "../global";

export default function AdminSubscriptionsList() {
    const location = useLocation();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    // tabs: "trial" | "paid" | "none"
    const [tab, setTab] = useState("trial");

    useEffect(() => {
        sessionStorage.setItem("previousPagePathname", location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        loadList();
    }, []);

    async function loadList() {
        setLoading(true);
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "listCentresWithLatestSubscription",
            });
            const data = Array.isArray(res?.data?.data) ? res.data.data : [];
            // normalize into grid rows
            const mapped = data.map((r, i) => ({
                id: i + 1,
                pk_cent_key: r.pk_cent_key,
                cent_name: r.cent_name,
                // if null/undefined, keep as empty string -> treated as "no subscription"
                subscription_type: (r.subscription_type || "").toLowerCase(),
                effective_date: r.effective_date || "",
                end_date: r.end_date || "",
            }));
            setRows(mapped);
        } catch {
            toast.error("Failed to load subscriptions");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }

    const filtered = useMemo(() => {
        if (tab === "trial") {
            return rows.filter((r) => r.subscription_type === "trial");
        }
        if (tab === "paid") {
            return rows.filter(
                (r) => r.subscription_type === "monthly" || r.subscription_type === "annual"
            );
        }
        // "none" -> no subscription_type present
        return rows.filter((r) => !r.subscription_type);
    }, [rows, tab]);

    const columns = [
        { field: "cent_name", headerName: "Centre", flex: 1, headerClassName: "super-app-theme--header" },
        {
            field: "subscription_type",
            headerName: "Type",
            flex: 1,
            headerClassName: "super-app-theme--header",
            valueFormatter: (p) =>
                p?.value ? p.value.charAt(0).toUpperCase() + p.value.slice(1) : "— none —",
        },
        { field: "effective_date", headerName: "Effective", flex: 1, headerClassName: "super-app-theme--header" },
        { field: "end_date", headerName: "End", flex: 1, headerClassName: "super-app-theme--header" },
        {
            field: "actions",
            headerName: "Actions",
            sortable: false,
            filterable: false,
            flex: 1,
            headerClassName: "super-app-theme--header",
            renderCell: (params) => {
                const row = params.row;
                return (
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={() => {
                            const state = { centKey: row.pk_cent_key, centName: row.cent_name };
                            sessionStorage.setItem("subsCentreState", JSON.stringify(state));
                            navigate("/admin/AdminSubscriptions/Centre", { state });
                        }}
                    >
                        View / Edit
                    </button>
                );
            },
        },
    ];

    return (
        <div className="container">
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="page-title">Centre Subscriptions</h5>
                    <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
                        <NavLink to="/calendar">Home</NavLink>
                        <NavLink to="/reports">Reports</NavLink>
                        <Typography color="text.primary">Subscriptions</Typography>
                    </Breadcrumbs>
                </div>
                <div className="d-flex gap-2">
                    <button className="btn btn-outline-secondary" onClick={loadList} disabled={loading}>
                        {loading ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-3">
                <div className="btn-group" role="group" aria-label="Subscription tabs">
                    <button
                        type="button"
                        className={`btn ${tab === "trial" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTab("trial")}
                    >
                        Trial
                    </button>
                    <button
                        type="button"
                        className={`btn ${tab === "paid" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTab("paid")}
                    >
                        Monthly / Annual
                    </button>
                    <button
                        type="button"
                        className={`btn ${tab === "none" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTab("none")}
                    >
                        No add-on Subscription
                    </button>
                </div>
            </div>

            <Card className="card my-card mb-3">
                <Card.Body>
                    <Box
                        sx={{
                            width: "100%",
                            overflowX: "auto",
                            "& .super-app-theme--header": { backgroundColor: "#f4f4f4", whiteSpace: "normal" },
                            "& .MuiDataGrid-cell": { whiteSpace: "normal", wordWrap: "break-word" },
                            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": { outline: "none" },
                        }}
                    >
                        <DataGrid
                            autoHeight
                            loading={loading}
                            rows={filtered}
                            columns={columns}
                            pageSizeOptions={[10, 25, 50, 100]}
                            rowHeight={56}
                            slots={{ loadingOverlay: LinearProgress, toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    csvOptions: { fileName: `Subscriptions_${tab}` },
                                    printOptions: { fileName: `Subscriptions_${tab}`, hideFooter: true, hideToolbar: true },
                                    showQuickFilter: true,
                                },
                            }}
                            disableRowSelectionOnClick
                            showColumnVerticalBorder
                            showCellVerticalBorder
                        />
                    </Box>
                </Card.Body>
            </Card>
        </div>
    );
}
